// HTTP wrapper for the table editor backend.
import { api } from './api.js';

export async function loadTableSchema(connectionId, database, table) {
  return api.get(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/schema`
  );
}

// Column data types offered for this connection's server, filtered + annotated by
// the server's resolved capabilities (JSON only where supported, fractional-seconds
// arg only where supported). Drives the table editor's type dropdown so a user can
// only pick a type the target accepts. Returns the array of types; [] on error so
// the editor still renders (it then preserves each column's existing type as-is).
export async function loadDataTypes(connectionId) {
  try {
    const r = await api.get(`/api/connections/${connectionId}/datatypes`);
    return r?.types ?? [];
  } catch {
    return [];
  }
}

// Inbound FK edges — other tables that reference this table's PK (or any
// unique key). Powers the editable-PK confirmation modal. Returns
// `{ references: [{ referencingSchema, referencingTable, constraintName,
//                   referencingColumns, referencedColumns, onUpdate, onDelete }] }`.
export async function loadInboundFKs(connectionId, database, table) {
  return api.get(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/fk-references`
  );
}

// Raw SHOW CREATE TABLE body, as returned by the source MySQL server. Used by
// the "Copy Schema (SQL)" right-click action and the single-table dump dialog
// (the dialog calls /api/table-dump, but this helper is the cheap-preview path).
// Returns `{ sql: "CREATE TABLE `...` (...)" }`.
export async function loadTableCreate(connectionId, database, table) {
  return api.get(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/create`
  );
}

export async function previewTableSchema(connectionId, database, table, edit) {
  return api.post(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/schema/preview`,
    edit
  );
}

// override: pass true to proceed despite a version-compatibility violation the
// preview call already surfaced (see startPreview/confirmApply in TableEditor.jsx)
// — otherwise the server blocks with 409 + the violation list.
export async function applyTableSchema(connectionId, database, table, edit, override) {
  return api.post(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/schema`,
    override ? { ...edit, override: true } : edit
  );
}

// New-table (CREATE) preview/apply. The table name isn't a path segment (it
// doesn't exist yet) — it travels in the body alongside the edit request.
export async function previewCreateTable(connectionId, database, table, edit) {
  return api.post(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/create-table/preview`,
    { table, ...edit }
  );
}

export async function createTable(connectionId, database, table, edit, override) {
  return api.post(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/create-table`,
    override ? { table, ...edit, override: true } : { table, ...edit }
  );
}
